import { createClient } from '@supabase/supabase-js';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { signOutWithLocalFallback } from './signOutWithLocalFallback';

// 환경변수 검증 - 실제 Supabase 연결 필수
// 프로덕션 환경에서의 fallback 처리 포함
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ||
    (window as any)?.__SUPABASE_URL__ ||
    process.env.VITE_SUPABASE_URL;

const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ||
    (window as any)?.__SUPABASE_ANON_KEY__ ||
    process.env.VITE_SUPABASE_ANON_KEY;

// 환경변수 상세 검증
if (!supabaseUrl || !supabaseAnonKey) {
    console.error('❌ Supabase 환경변수 누락:', {
        VITE_SUPABASE_URL: supabaseUrl ? '✅ 설정됨' : '❌ 누락',
        VITE_SUPABASE_ANON_KEY: supabaseAnonKey ? '✅ 설정됨' : '❌ 누락'
    });
    throw new Error('Missing Supabase environment variables. Please check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
}

// URL 형식 검증
try {
    new URL(supabaseUrl);
} catch (error) {
    console.error('❌ Supabase URL 형식 오류:', supabaseUrl);
    throw new Error('Invalid Supabase URL format. Please check VITE_SUPABASE_URL.');
}

// Supabase 클라이언트 생성 (참고 코드 기반 안정성 개선)
let supabaseClient;
try {
    supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true,
            // 전화번호 인증을 위한 추가 설정
            flowType: 'pkce',
            // 잘못된 refresh token 처리를 위한 설정
            storage: {
                getItem: (key) => {
                    try {
                        return window.localStorage.getItem(key);
                    } catch {
                        return null;
                    }
                },
                setItem: (key, value) => {
                    try {
                        window.localStorage.setItem(key, value);
                    } catch {
                        console.error('Failed to save to localStorage');
                    }
                },
                removeItem: (key) => {
                    try {
                        window.localStorage.removeItem(key);
                    } catch {
                        console.error('Failed to remove from localStorage');
                    }
                }
            }
        }
    });

    // Functions API 가용성 검증
    if (!supabaseClient.functions) {
        console.error('❌ Supabase Functions API를 사용할 수 없습니다.');
        throw new Error('Supabase Functions API is not available');
    }

    console.log('✅ Supabase 클라이언트 초기화 완료 (전화번호 인증 지원)');
} catch (error) {
    console.error('❌ Supabase 클라이언트 초기화 실패:', error);
    throw new Error('Failed to initialize Supabase client');
}

export const supabase = supabaseClient;

// 데이터베이스 타입 정의
export interface User {
    id: string;
    email: string;
    instagram_username?: string;
    created_at: string;
    updated_at: string;
    subscription_tier: 'free' | 'premium' | 'pro';
    subscription_expires_at?: string;
}

export interface InstagramProfile {
    id: string;
    user_id: string;
    instagram_id: string;
    username: string;
    full_name: string;
    biography?: string;
    profile_picture_url?: string;
    followers_count: number;
    following_count: number;
    posts_count: number;
    is_private: boolean;
    is_verified: boolean;
    external_url?: string;
    created_at: string;
    updated_at: string;
    last_synced_at: string;
}

export interface Visitor {
    id: string;
    profile_id: string;
    visitor_instagram_id?: string;
    visitor_username?: string;
    visitor_name?: string;
    visitor_avatar_url?: string;
    location?: Record<string, any>;
    device_info: Record<string, any>;
    user_agent: string;
    ip_address?: string;
    visit_duration?: number;
    pages_viewed: number;
    referrer?: string;
    session_id: string;
    is_returning_visitor: boolean;
    created_at: string;
    visit_count: number;
    last_visit_at: string;
}

export interface Analytics {
    id: string;
    profile_id: string;
    date: string;
    total_views: number;
    unique_visitors: number;
    avg_session_duration: number;
    bounce_rate: number;
    top_countries: Record<string, number>;
    top_devices: Record<string, number>;
    top_referrers: Record<string, number>;
    hourly_data: Record<string, number>;
    created_at: string;
}

// 전화번호 인증 함수들
export const auth = {
    // 현재 사용자 가져오기
    getCurrentUser: async (): Promise<SupabaseUser | null> => {
        try {
            const { data: { user }, error } = await supabase.auth.getUser();

            // 인증 관련 에러 처리 (403, 401, Refresh token 등)
            if (error) {
                const errorMessage = error.message?.toLowerCase() || '';
                const isAuthError =
                    errorMessage.includes('invalid refresh token') ||
                    errorMessage.includes('refresh token not found') ||
                    errorMessage.includes('invalid jwt') ||
                    errorMessage.includes('jwt expired') ||
                    errorMessage.includes('not authenticated') ||
                    error.status === 403 ||
                    error.status === 401;

                if (isAuthError) {
                    console.log('ℹ️ 세션 없음 또는 만료 - 정리 중...');

                    // 조용히 세션 정리 시도
                    try {
                        await supabase.auth.signOut();
                    } catch {
                        // signOut 실패해도 무시
                    }

                    // localStorage에서 Supabase 관련 데이터 제거
                    const keys = Object.keys(localStorage);
                    keys.forEach(key => {
                        if (key.includes('supabase') || key === 'auth-storage' || key === 'instagram-store') {
                            localStorage.removeItem(key);
                        }
                    });

                    return null;
                }

                // 네트워크·일시 오류(타임아웃·5xx 등) — 진짜 만료가 아니므로 로그아웃하지 않는다.
                // 로컬에 유효한 세션이 있으면 그 user로 로그인 유지 (지하철·와이파이 전환 시
                // 세션이 멀쩡한데 로그아웃되던 버그 수리). 로컬 세션도 없을 때만 null.
                console.warn('⚠️ getUser 일시 오류 - 로컬 세션 유지 시도:', error.message);
                const { data: { session: fallbackSession } } = await supabase.auth.getSession();
                return fallbackSession?.user ?? null;
            }

            if (!user) return null;
            return user;
        } catch (error: any) {
            // 네트워크 예외 — 로컬 세션이 살아있으면 로그인 유지 (오프라인·순간 단절 방어)
            console.log('ℹ️ getCurrentUser 예외 - 로컬 세션 유지 시도');
            try {
                const { data: { session: fallbackSession } } = await supabase.auth.getSession();
                return fallbackSession?.user ?? null;
            } catch {
                return null;
            }
        }
    },

    // 전화번호로 OTP 전송 (참고 코드 로직 적용)
    signInWithPhone: async (phone: string): Promise<void> => {
        console.log('🔐 전화번호 OTP 전송 시도:', phone);
        
        try {
            // 참고 코드와 동일한 전화번호 형식 변환 로직
            let formattedE164Phone = phone.replace(/\s|-/g, "");
            if (formattedE164Phone.startsWith("0")) {
                formattedE164Phone = "+82" + formattedE164Phone.substring(1);
            } else if (!formattedE164Phone.startsWith("+")) {
                formattedE164Phone = "+82" + formattedE164Phone;
            }
            
            console.log('📱 E164 형식 변환:', phone, '→', formattedE164Phone);
            
            // 참고 코드와 동일한 signInWithOtp 호출
            const { error: smsError } = await supabase.auth.signInWithOtp({ 
                phone: formattedE164Phone 
            });
            
            if (smsError) {
                console.error('❌ SMS 전송 실패:', smsError);
                throw smsError;
            }
            
            console.log('✅ OTP 전송 성공');
        } catch (error) {
            console.error('❌ OTP 전송 과정에서 오류:', error);
            throw error;
        }
    },

    // 전화번호 회원가입 (OTP 전송)
    signUpWithPhone: async (phone: string): Promise<void> => {
        console.log('📝 전화번호 회원가입 OTP 전송:', phone);
        
        // 회원가입도 동일하게 OTP로 처리 (참고 코드와 동일)
        await auth.signInWithPhone(phone);
    },

    // OTP 검증 및 로그인/회원가입 완료 (참고 코드 로직 적용)
    verifyPhoneOtp: async (phone: string, token: string): Promise<SupabaseUser> => {
        console.log('🔍 OTP 검증 시도:', phone, token);
        
        try {
            // 참고 코드와 동일한 전화번호 형식 변환
            let formattedE164Phone = phone.replace(/\s|-/g, "");
            if (formattedE164Phone.startsWith("0")) {
                formattedE164Phone = "+82" + formattedE164Phone.substring(1);
            } else if (!formattedE164Phone.startsWith("+")) {
                formattedE164Phone = "+82" + formattedE164Phone;
            }
            
            console.log('📱 OTP 검증용 E164 형식:', formattedE164Phone);
            
            // 참고 코드와 동일한 verifyOtp 호출
            const { error: verifyError } = await supabase.auth.verifyOtp({ 
                phone: formattedE164Phone, 
                token: token, 
                type: 'sms' 
            });
            
            if (verifyError) {
                console.error('❌ OTP 검증 실패:', verifyError);
                throw verifyError;
            }
            
            // 사용자 정보 가져오기
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                throw new Error('사용자 정보를 가져올 수 없습니다');
            }
            
            console.log('✅ OTP 검증 성공:', user);
            return user;
        } catch (error) {
            console.error('❌ OTP 검증 과정에서 오류:', error);
            throw error;
        }
    },

    // 로그아웃 — 서버가 거절해도(탈퇴 직후 계정·서버 오류) 이 기기에서는 로그아웃 (2026-10-28)
    signOut: (): Promise<void> => signOutWithLocalFallback(supabase, supabaseUrl),

    // 인증 상태 변화 감지
    onAuthStateChange: (callback: (user: SupabaseUser | null) => void) => {
        return supabase.auth.onAuthStateChange((_event, session) => {
            callback(session?.user || null);
        });
    }
};

// 전화번호 표시용 포맷팅 함수 (참고 코드 로직 적용)
export const formatPhoneDisplay = (input: string): string => {
    const digits: string = input.replace(/\D/g, '').slice(0, 11);
    const part1: string = digits.slice(0, 3);
    const part2: string = digits.slice(3, 7);
    const part3: string = digits.slice(7, 11);
    let formattedPhoneString: string = part1;
    if (digits.length > 3) formattedPhoneString += '-' + part2;
    if (digits.length > 7) formattedPhoneString += '-' + part3;
    return formattedPhoneString;
};

// 사용자 프로필 관련 함수들
export const profiles = {
    // 사용자 프로필 가져오기
    getProfile: async (userId: string): Promise<User | null> => {
        try {
            const { data, error } = await supabase
                .from('users')
                .select('*')
                .eq('id', userId)
                .single();

            if (error) {
                if (error.code === 'PGRST116') return null; // Not found
                throw error;
            }
            return data;
        } catch (error) {
            console.warn('프로필 로드 실패, 데모 데이터 반환');
            return null;
        }
    },

    // 사용자 프로필 업데이트
    updateProfile: async (userId: string, updates: Partial<User>) => {
        try {
            const { data, error } = await supabase
                .from('users')
                .update({ ...updates, updated_at: new Date().toISOString() })
                .eq('id', userId)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('프로필 업데이트 실패:', error);
            throw new Error('프로필 업데이트에 실패했습니다.');
        }
    },

    // Instagram 프로필 연결
    linkInstagramProfile: async (userId: string, instagramData: Partial<InstagramProfile>) => {
        try {
            const { data, error } = await supabase
                .from('instagram_profiles')
                .upsert({
                    user_id: userId,
                    ...instagramData,
                    updated_at: new Date().toISOString(),
                    last_synced_at: new Date().toISOString()
                })
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('Instagram 프로필 연결 실패:', error);
            throw new Error('Instagram 프로필 연결에 실패했습니다.');
        }
    }
};

// 저장된 Instagram 계정 관련 함수들 (Edge Function과 연동)
export const savedInstagramAccounts = {
    // 사용자의 저장된 Instagram 계정들 가져오기
    getByUserId: async (userId: string) => {
        try {
            const { data, error } = await supabase
                .from('saved_instagram_accounts')
                .select('*')
                .eq('user_id', userId)
                .order('updated_at', { ascending: false });

            if (error) throw error;

            console.log(`✅ 저장된 Instagram 계정 조회 성공 (user: ${userId}):`, data?.length || 0, '개');
            return data || [];
        } catch (error) {
            console.warn(`저장된 Instagram 계정 로드 실패:`, error);
            return [];
        }
    },

    // 사용자 본인의 계정 조회 (연결된 계정 페이지용)
    getUserOwnedAccounts: async (userId: string) => {
        console.log(`🔍 사용자 본인 계정 조회: ${userId}`);
        return await savedInstagramAccounts.getByUserId(userId);
    },

    // 단일 저장된 Instagram 계정 가져오기
    getByUsername: async (userId: string, username: string) => {
        try {
            const { data, error } = await supabase
                .from('saved_instagram_accounts')
                .select('*')
                .eq('user_id', userId)
                .eq('username', username)
                .maybeSingle();

            if (error) {
                if (error.code === 'PGRST116') return null;
                throw error;
            }
            return data;
        } catch (error) {
            console.warn(`저장된 Instagram 계정 조회 실패:`, error);
            return null;
        }
    }
};

// 공개 Instagram 게시물 관련 함수들
export const publicInstagramPosts = {
    // 특정 사용자의 게시물들 가져오기
    getByUserId: async (userId: string, limit = 12) => {
        try {
            console.log(`[DB] 사용자 ID로 게시물 조회: ${userId}`);
            const { data, error } = await supabase
                .from('public_instagram_posts')
                .select('*')
                .eq('user_id', userId)
                .order('created_at', { ascending: false })
                .limit(limit);

            if (error) {
                console.error('[DB Error] user_id로 게시물 조회 실패:', error);
                throw error;
            }
            console.log(`[DB] 사용자 ID로 ${data?.length || 0}개 게시물 조회 성공`);
            return data || [];
        } catch (error) {
            console.warn('공개 Instagram 게시물 로드 실패:', error);
            return [];
        }
    },

    // 특정 Instagram 아이디의 게시물들 가져오기  
    getByInstagramId: async (instagramId: string, limit = 12) => {
        try {
            console.log(`[DB] Instagram ID로 게시물 조회: ${instagramId}`);
            const { data, error } = await supabase
                .from('public_instagram_posts')
                .select('*')
                .eq('instagram_id', instagramId)
                .order('created_at', { ascending: false })
                .limit(limit);

            if (error) {
                console.error('[DB Error] instagram_id로 게시물 조회 실패:', error);
                throw error;
            }
            console.log(`[DB] Instagram ID로 ${data?.length || 0}개 게시물 조회 성공`);
            return data || [];
        } catch (error) {
            console.warn('Instagram ID로 게시물 로드 실패:', error);
            return [];
        }
    },

    // 특정 Instagram username의 게시물들 가져오기
    getByUsername: async (username: string, limit = 12) => {
        try {
            console.log(`[DB] Instagram username으로 게시물 조회: ${username}`);
            const { data, error } = await supabase
                .from('public_instagram_posts')
                .select('*')
                .eq('instagram_username', username) // username → instagram_username으로 수정
                .order('created_at', { ascending: false })
                .limit(limit);

            if (error) {
                console.error('[DB Error] instagram_username으로 게시물 조회 실패:', error);
                throw error;
            }
            console.log(`[DB] Instagram username으로 ${data?.length || 0}개 게시물 조회 성공`);
            return data || [];
        } catch (error) {
            console.warn('Username으로 게시물 로드 실패:', error);
            return [];
        }
    },

    // 테이블 구조 확인용 함수 (디버깅용)
    checkTableStructure: async () => {
        try {
            console.log('[DB] public_instagram_posts 테이블 구조 확인 중...');
            const { data, error } = await supabase
                .from('public_instagram_posts')
                .select('*')
                .limit(1);

            if (error) {
                console.error('[DB Error] 테이블 구조 확인 실패:', error);
                return null;
            }

            if (data && data.length > 0) {
                console.log('[DB] 테이블 구조 (첫 번째 행의 컬럼들):', Object.keys(data[0]));
                console.log('[DB] 샘플 데이터:', data[0]);
            } else {
                console.log('[DB] 테이블이 비어있습니다.');
            }

            return data;
        } catch (error) {
            console.error('[DB] 테이블 구조 확인 중 오류:', error);
            return null;
        }
    }
};

// Instagram 프로필 관련 함수들
export const instagramProfiles = {
    // 사용자의 Instagram 프로필들 가져오기 (배열)
    getByUserId: async (userId: string): Promise<InstagramProfile[]> => {
        try {
            const { data, error } = await supabase
                .from('instagram_profiles')
                .select('*')
                .eq('user_id', userId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            return data || [];
        } catch (error) {
            console.warn('Instagram 프로필 로드 실패, 데모 데이터 반환');
            return [];
        }
    },

    // 단일 Instagram 프로필 가져오기
    getById: async (profileId: string): Promise<InstagramProfile | null> => {
        try {
            const { data, error } = await supabase
                .from('instagram_profiles')
                .select('*')
                .eq('id', profileId)
                .single();

            if (error) {
                if (error.code === 'PGRST116') return null;
                throw error;
            }
            return data;
        } catch (error) {
            console.warn('Instagram 프로필 조회 실패');
            return null;
        }
    },

    // Instagram 프로필 생성/업서트
    upsert: async (profileData: Omit<InstagramProfile, 'id' | 'created_at' | 'updated_at'>) => {
        try {
            const { data, error } = await supabase
                .from('instagram_profiles')
                .upsert({
                    ...profileData,
                    updated_at: new Date().toISOString(),
                    last_synced_at: new Date().toISOString()
                }, {
                    onConflict: 'user_id, username'
                })
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('Instagram 프로필 저장 실패:', error);
            throw new Error('Instagram 프로필 저장에 실패했습니다.');
        }
    },

    // Instagram 프로필 업데이트
    update: async (profileId: string, updates: Partial<InstagramProfile>) => {
        try {
            const { data, error } = await supabase
                .from('instagram_profiles')
                .update({ ...updates, updated_at: new Date().toISOString() })
                .eq('id', profileId)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('Instagram 프로필 업데이트 실패:', error);
            throw new Error('Instagram 프로필 업데이트에 실패했습니다.');
        }
    },

    // Instagram 프로필 삭제
    delete: async (profileId: string) => {
        try {
            const { error } = await supabase
                .from('instagram_profiles')
                .delete()
                .eq('id', profileId);

            if (error) throw error;
        } catch (error) {
            console.error('Instagram 프로필 삭제 실패:', error);
            throw new Error('Instagram 프로필 삭제에 실패했습니다.');
        }
    }
};

// 방문자 관련 함수들
export const visitors = {
    // 프로필의 방문자 목록 가져오기
    getByProfileId: async (profileId: string, limit = 50, offset = 0) => {
        const { data, error } = await supabase
            .from('visitors')
            .select('*')
            .eq('profile_id', profileId)
            .order('last_visit_at', { ascending: false })
            .range(offset, offset + limit - 1);

        if (error) throw error;
        return data;
    },

    // 방문자 추가/업데이트
    upsert: async (visitorData: Omit<Visitor, 'id' | 'created_at' | 'last_visit_at' | 'visit_count'>) => {
        const { data, error } = await supabase
            .from('visitors')
            .upsert({
                ...visitorData,
                created_at: new Date().toISOString(),
                last_visit_at: new Date().toISOString(),
                visit_count: 1
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    // 방문자 통계 가져오기
    getStats: async (profileId: string, days = 7) => {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);

        const { data, error } = await supabase
            .from('visitors')
            .select('*')
            .eq('profile_id', profileId)
            .gte('created_at', startDate.toISOString());

        if (error) throw error;
        return data;
    }
};

// 분석 데이터 관련 함수들
export const analytics = {
    // 프로필의 분석 데이터 가져오기
    getByProfileId: async (profileId: string, startDate: string, endDate: string) => {
        const { data, error } = await supabase
            .from('analytics')
            .select('*')
            .eq('profile_id', profileId)
            .gte('date', startDate)
            .lte('date', endDate)
            .order('date', { ascending: false });

        if (error) throw error;
        return data;
    },

    // 분석 데이터 업서트
    upsert: async (analyticsData: Omit<Analytics, 'id' | 'created_at'>) => {
        const { data, error } = await supabase
            .from('analytics')
            .upsert({
                ...analyticsData,
                created_at: new Date().toISOString()
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    }
};

// 실시간 구독 설정
export const realtime = {
    // 방문자 실시간 구독
    subscribeToVisitors: (profileId: string, callback: (payload: any) => void) => {
        return supabase
            .channel(`visitors:${profileId}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'visitors',
                    filter: `profile_id=eq.${profileId}`
                },
                callback
            )
            .subscribe();
    },

    // 분석 데이터 실시간 구독
    subscribeToAnalytics: (profileId: string, callback: (payload: any) => void) => {
        return supabase
            .channel(`analytics:${profileId}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'analytics',
                    filter: `profile_id=eq.${profileId}`
                },
                callback
            )
            .subscribe();
    }
};

// 유틸리티 함수들
export const utils = {
    // 파일 업로드
    uploadFile: async (bucket: string, path: string, file: File) => {
        const { data, error } = await supabase.storage
            .from(bucket)
            .upload(path, file, {
                cacheControl: '3600',
                upsert: false
            });

        if (error) throw error;
        return data;
    },

    // 파일 URL 가져오기
    getFileUrl: (bucket: string, path: string) => {
        const { data } = supabase.storage
            .from(bucket)
            .getPublicUrl(path);

        return data.publicUrl;
    }
};

// 방문자 분석 관련 함수들
export const visitorAnalysis = {
    // 분석 요청 생성 (실제 분석 데이터는 백엔드에서 안전하게 생성됨)
    createRequest: async (userId: string, targetUsername: string, targetProfileData: any) => {
        try {
            const { data, error } = await supabase
                .from('visitor_analysis_requests')
                .insert({
                    user_id: userId,
                    target_instagram_username: targetUsername,
                    target_profile_data: targetProfileData,
                    analysis_status: 'pending' // 백엔드에서 처리 후 'completed'로 변경
                })
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('분석 요청 생성 실패:', error);
            throw error;
        }
    },

    // 결과서 생성
    createReport: async (requestId: string, userId: string, targetUsername: string, reportData: any) => {
        try {
            const { data, error } = await supabase
                .from('analysis_reports')
                .insert({
                    request_id: requestId,
                    user_id: userId,
                    target_username: targetUsername,
                    report_data: reportData,
                    is_accessible: false // 결제 전까지는 접근 불가
                })
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('결과서 생성 실패:', error);
            throw error;
        }
    },

    // 결제 완료 후 결과서 접근 권한 부여
    enableReportAccess: async (requestId: string) => {
        try {
            const { data, error } = await supabase
                .from('analysis_reports')
                .update({ is_accessible: true })
                .eq('request_id', requestId)
                .select()
                .single();

            if (error) throw error;

            // 요청의 결제 상태도 업데이트
            await supabase
                .from('visitor_analysis_requests')
                .update({ payment_status: 'paid' })
                .eq('id', requestId);

            return data;
        } catch (error) {
            console.error('결과서 접근 권한 부여 실패:', error);
            throw error;
        }
    },

    // 사용자의 분석 요청 목록 조회
    getUserRequests: async (userId: string) => {
        try {
            const { data, error } = await supabase
                .from('visitor_analysis_requests')
                .select('*, analysis_reports(*)')
                .eq('user_id', userId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            return data || [];
        } catch (error) {
            console.error('분석 요청 목록 조회 실패:', error);
            return [];
        }
    },

    // 특정 결과서 조회
    getReport: async (requestId: string, userId: string) => {
        try {
            const { data, error } = await supabase
                .from('analysis_reports')
                .select('*')
                .eq('request_id', requestId)
                .eq('user_id', userId)
                .eq('is_accessible', true)
                .single();

            if (error) {
                if (error.code === 'PGRST116') return null;
                throw error;
            }

            // 조회 시간 업데이트
            await supabase
                .from('analysis_reports')
                .update({ viewed_at: new Date().toISOString() })
                .eq('id', data.id);

            return data;
        } catch (error) {
            console.error('결과서 조회 실패:', error);
            return null;
        }
    }
};

// 결제 관련 함수들
export const payments = {
    // 결제 기록 생성
    createPayment: async (userId: string, requestId: string, stripePaymentIntentId: string, amount: number) => {
        try {
            const { data, error } = await supabase
                .from('payments')
                .insert({
                    user_id: userId,
                    request_id: requestId,
                    stripe_payment_intent_id: stripePaymentIntentId,
                    amount: amount,
                    status: 'succeeded'
                })
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('결제 기록 생성 실패:', error);
            throw error;
        }
    },

    // 사용자의 결제 내역 조회
    getUserPayments: async (userId: string) => {
        try {
            const { data, error } = await supabase
                .from('payments')
                .select('*, visitor_analysis_requests(*)')
                .eq('user_id', userId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            return data || [];
        } catch (error) {
            console.error('결제 내역 조회 실패:', error);
            return [];
        }
    }
};

export default supabase; 